<script setup lang="ts">
import type { Article } from '@/types'
import { primaryCategory } from '@/content'
import { useBlogStore, primaryCategoryIn } from '@/lib/blog'
import { formatDate } from '@/lib/format'

const props = defineProps<{ article: Article }>()
// Categories live du blog en priorite ; repli sur celles du build hors blog.
const blog = useBlogStore()
const cat = blog.payload?.categories?.length
  ? primaryCategoryIn(props.article, blog.payload.categories)
  : primaryCategory(props.article)
</script>

<template>
  <article class="card acard">
    <router-link :to="article.path" class="acard__img" :aria-label="article.title">
      <img
        v-if="article.featured_image"
        :src="article.featured_image"
        :alt="article.title"
        loading="lazy"
        width="400"
        height="240"
      />
      <div v-else class="acard__ph" aria-hidden="true"></div>
    </router-link>
    <div class="acard__body">
      <div class="acard__meta">
        <span v-if="cat" class="acard__cat">{{ cat.name }}</span>
        <span class="acard__date">{{ formatDate(article.date) }}</span>
      </div>
      <h3 class="acard__title">
        <router-link :to="article.path">{{ article.title }}</router-link>
      </h3>
      <p v-if="article.excerpt" class="acard__excerpt">{{ article.excerpt }}</p>
      <router-link :to="article.path" class="acard__more">Lire l'article →</router-link>
    </div>
  </article>
</template>

<style scoped>
.acard__img {
  display: block;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: var(--bg-alt);
}
.acard__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}
.acard:hover .acard__img img {
  transform: scale(1.04);
}
.acard__ph {
  width: 100%;
  height: 100%;
  background: linear-gradient(120deg, #eef2e0, #dfe7c8);
}
.acard__body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.acard__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.78rem;
  margin-bottom: 8px;
}
.acard__cat {
  background: var(--green-soft);
  color: var(--green-dark);
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
}
.acard__date {
  color: var(--muted);
}
.acard__title {
  font-size: 1.08rem;
  margin: 0 0 8px;
}
.acard__title a {
  color: var(--title);
}
.acard__title a:hover {
  color: var(--green-dark);
  text-decoration: none;
}
.acard__excerpt {
  color: var(--muted);
  font-size: 0.9rem;
  margin: 0 0 14px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.acard__more {
  margin-top: auto;
  color: var(--green-dark);
  font-weight: 700;
  font-size: 0.9rem;
}
</style>
